/* eslint-disable typescript-extended-rules/file-naming-convention */
import { Component, Inject } from '@angular/core';
import {
	UntypedFormArray,
	UntypedFormBuilder,
	UntypedFormControl,
	UntypedFormGroup,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import {
	BeneficiaryReconciliationMappingAnalysisResponse,
	BeneficiaryReconciliationMappingBenefitKind,
	BeneficiaryReconciliationMappingCanonicalField,
	BeneficiaryReconciliationMappingColumn,
	BeneficiaryReconciliationMappingConfirmation,
	BeneficiaryReconciliationMappingFieldState,
	BeneficiaryReconciliationMappingSourceAnalysis,
	BeneficiaryReconciliationMappingSourceUsage,
} from '../../../../../modules/beneficiary-reconciliations/models/beneficiary-reconciliation.model';

import {
	BENEFICIARY_RECONCILIATION_MAPPING_BENEFIT_OPTIONS,
	BENEFICIARY_RECONCILIATION_MAPPING_FIELD_METADATA,
	BENEFICIARY_RECONCILIATION_MAPPING_TITULARITY_OPTIONS,
	BeneficiaryReconciliationMappingFieldMetadata,
	getMappingSpecialStateLabel,
	mappingFieldAllowsMissingFromSource,
	mappingFieldAllowsNotApplicable,
} from './beneficiary-reconciliation-mapping-modal.constants';
import {
	MappingSourceDraft,
	buildMappingConfirmation,
	buildMappingValidationErrors,
	getMappingColumnOptionValue,
	getMappingRequiredFields,
	getMappingSpecialOptionValue,
	getVisibleMappingFields,
} from './beneficiary-reconciliation-mapping-modal.helpers';

const EXCEL_COLUMN_ALPHABET_LENGTH = 26;
const EXCEL_COLUMN_FIRST_LETTER_CODE = 'A'.charCodeAt(0);
type ContextualColumnAliasGroups = readonly (readonly string[])[];
const GENERIC_CARD_COLUMN_ALIAS_GROUPS: ContextualColumnAliasGroups = [
	['carteirinha'],
	[
		'carteira',
		'cartao',
		'codigo carteirinha',
		'codigo da carteirinha',
		'numero cartao',
		'numero do cartao',
	],
] as const;
const GENERIC_PLAN_COLUMN_ALIAS_GROUPS: ContextualColumnAliasGroups = [
	['plano', 'beneficio'],
	['descricao do plano', 'descricao plano', 'produto'],
] as const;
const CONTEXTUAL_BENEFIT_FIELDS = [
	BeneficiaryReconciliationMappingCanonicalField.HEALTH_PLAN_NAME,
	BeneficiaryReconciliationMappingCanonicalField.HEALTH_CARD_NUMBER,
	BeneficiaryReconciliationMappingCanonicalField.DENTAL_PLAN_NAME,
	BeneficiaryReconciliationMappingCanonicalField.DENTAL_CARD_NUMBER,
	BeneficiaryReconciliationMappingCanonicalField.CONJUGATED_PLAN_NAME,
	BeneficiaryReconciliationMappingCanonicalField.CONJUGATED_CARD_NUMBER,
] as const;

export interface BeneficiaryReconciliationMappingModalData {
	analysis: BeneficiaryReconciliationMappingAnalysisResponse;
}

@Component({
	selector: 'app-beneficiary-reconciliation-mapping-modal',
	styleUrls: ['./beneficiary-reconciliation-mapping-modal.component.scss'],
	templateUrl: './beneficiary-reconciliation-mapping-modal.component.html',
})
export class BeneficiaryReconciliationMappingModalComponent {
	readonly benefitKindOptions =
		BENEFICIARY_RECONCILIATION_MAPPING_BENEFIT_OPTIONS;
	errorMessage = '';
	readonly fieldMetadata = BENEFICIARY_RECONCILIATION_MAPPING_FIELD_METADATA;
	readonly fieldState = BeneficiaryReconciliationMappingFieldState;
	form: UntypedFormGroup;
	readonly sourceUsage = BeneficiaryReconciliationMappingSourceUsage;
	readonly titularityKindOptions =
		BENEFICIARY_RECONCILIATION_MAPPING_TITULARITY_OPTIONS;
	private readonly contextualSuggestionValues = new Map<string, string>();

	constructor(
		@Inject(MAT_DIALOG_DATA)
		readonly data: BeneficiaryReconciliationMappingModalData,
		private readonly dialogRef: MatDialogRef<
			BeneficiaryReconciliationMappingModalComponent,
			BeneficiaryReconciliationMappingConfirmation
		>,
		private readonly fb: UntypedFormBuilder,
	) {
		this.form = this.fb.group({
			sources: this.fb.array(
				data.analysis.sources.map((source) =>
					this.createSourceFormGroup(source),
				),
			),
		});

		this.form.valueChanges.subscribe(() => {
			this.errorMessage = '';
		});
	}

	get sourceForms(): UntypedFormGroup[] {
		return (this.form.get('sources') as UntypedFormArray)
			.controls as UntypedFormGroup[];
	}

	getColumnDisplayLabel(
		column: BeneficiaryReconciliationMappingColumn,
	): string {
		return `${this.getExcelColumnLetter(column.index)} - ${column.sourceName}`;
	}

	getColumnOptionValue(columnName: string): string {
		return getMappingColumnOptionValue(columnName);
	}

	getFieldControl(
		sourceIndex: number,
		field: BeneficiaryReconciliationMappingCanonicalField,
	): UntypedFormControl {
		return this.sourceForms[sourceIndex]
			.get('fields')
			?.get(field) as UntypedFormControl;
	}

	getFieldStateOptionValue(
		state: BeneficiaryReconciliationMappingFieldState,
	): string {
		return getMappingSpecialOptionValue(state);
	}

	getSourceForm(sourceIndex: number): UntypedFormGroup {
		return this.sourceForms[sourceIndex];
	}

	getSourceOptionColumns(
		source: BeneficiaryReconciliationMappingSourceAnalysis,
	): BeneficiaryReconciliationMappingColumn[] {
		return [...source.columns].sort(
			(current, next) => current.index - next.index,
		);
	}

	getSpecialStateLabel(
		state: BeneficiaryReconciliationMappingFieldState,
	): string {
		return getMappingSpecialStateLabel(state);
	}

	getVisibleFieldMetadata(
		sourceIndex: number,
	): BeneficiaryReconciliationMappingFieldMetadata[] {
		const fieldMetadataByValue = new Map(
			this.fieldMetadata.map((fieldMetadata) => [
				fieldMetadata.value,
				fieldMetadata,
			]),
		);

		return this.getVisibleFields(sourceIndex).reduce<
			BeneficiaryReconciliationMappingFieldMetadata[]
		>((visibleFieldMetadata, field) => {
			const fieldMetadata = fieldMetadataByValue.get(field);

			if (fieldMetadata) {
				visibleFieldMetadata.push(fieldMetadata);
			}

			return visibleFieldMetadata;
		}, []);
	}

	isFieldRequired(
		sourceIndex: number,
		field: BeneficiaryReconciliationMappingCanonicalField,
	): boolean {
		return this.getRequiredFields(sourceIndex).includes(field);
	}

	isSourceIgnored(sourceIndex: number): boolean {
		return (
			this.getSourceForm(sourceIndex).get('usage')?.value ===
			BeneficiaryReconciliationMappingSourceUsage.IGNORE
		);
	}

	isSourceUsed(sourceIndex: number): boolean {
		return !this.isSourceIgnored(sourceIndex);
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	onConfirm(): void {
		const validationError = buildMappingValidationErrors(
			this.buildSourceDrafts(),
		)[0];

		if (validationError) {
			this.errorMessage = validationError;
		} else {
			this.dialogRef.close(buildMappingConfirmation(this.buildSourceDrafts()));
		}
	}

	shouldShowMissingFromSourceOption(
		sourceIndex: number,
		field: BeneficiaryReconciliationMappingCanonicalField,
	): boolean {
		return mappingFieldAllowsMissingFromSource(
			field,
			this.getSourceForm(sourceIndex).get('benefitKind')?.value ?? null,
		);
	}

	shouldShowNotApplicableOption(
		sourceIndex: number,
		field: BeneficiaryReconciliationMappingCanonicalField,
	): boolean {
		return mappingFieldAllowsNotApplicable(field);
	}

	trackByColumn(
		index: number,
		column: BeneficiaryReconciliationMappingColumn,
	): string {
		return column.sourceName;
	}

	trackByField(
		index: number,
		field: BeneficiaryReconciliationMappingFieldMetadata,
	): string {
		return field.value;
	}

	trackByIndex(index: number): number {
		return index;
	}

	trackByOptionValue(index: number, option: { value: string }): string {
		return option.value;
	}

	private applyContextualBenefitSuggestions(
		source: BeneficiaryReconciliationMappingSourceAnalysis,
		sourceForm: UntypedFormGroup,
	): void {
		const benefitKind = sourceForm.get('benefitKind')?.value;

		this.clearInvalidContextualSuggestions(source, sourceForm, benefitKind);
		this.syncConjugatedPrincipalDefaults(sourceForm, benefitKind);

		switch (benefitKind) {
			case BeneficiaryReconciliationMappingBenefitKind.HEALTH:
				this.applyContextualFieldSuggestion(
					source,
					sourceForm,
					BeneficiaryReconciliationMappingCanonicalField.HEALTH_PLAN_NAME,
					GENERIC_PLAN_COLUMN_ALIAS_GROUPS,
				);
				this.applyContextualFieldSuggestion(
					source,
					sourceForm,
					BeneficiaryReconciliationMappingCanonicalField.HEALTH_CARD_NUMBER,
					GENERIC_CARD_COLUMN_ALIAS_GROUPS,
				);
				break;
			case BeneficiaryReconciliationMappingBenefitKind.DENTAL:
				this.applyContextualFieldSuggestion(
					source,
					sourceForm,
					BeneficiaryReconciliationMappingCanonicalField.DENTAL_PLAN_NAME,
					GENERIC_PLAN_COLUMN_ALIAS_GROUPS,
				);
				this.applyContextualFieldSuggestion(
					source,
					sourceForm,
					BeneficiaryReconciliationMappingCanonicalField.DENTAL_CARD_NUMBER,
					GENERIC_CARD_COLUMN_ALIAS_GROUPS,
				);
				break;
			case BeneficiaryReconciliationMappingBenefitKind.CONJUGATED:
				this.applyContextualFieldSuggestion(
					source,
					sourceForm,
					BeneficiaryReconciliationMappingCanonicalField.CONJUGATED_PLAN_NAME,
					GENERIC_PLAN_COLUMN_ALIAS_GROUPS,
				);
				this.applyContextualFieldSuggestion(
					source,
					sourceForm,
					BeneficiaryReconciliationMappingCanonicalField.CONJUGATED_CARD_NUMBER,
					GENERIC_CARD_COLUMN_ALIAS_GROUPS,
				);
				break;
			default:
				break;
		}
	}

	private applyContextualFieldSuggestion(
		source: BeneficiaryReconciliationMappingSourceAnalysis,
		sourceForm: UntypedFormGroup,
		field: BeneficiaryReconciliationMappingCanonicalField,
		aliasGroups: ContextualColumnAliasGroups,
	): void {
		const control = sourceForm
			.get('fields')
			?.get(field) as UntypedFormControl | null;

		if (!control || control.value) {
			return;
		}

		const column = this.findContextualColumn(source, aliasGroups);

		if (column) {
			const value = this.getColumnOptionValue(column.sourceName);

			this.setContextualSuggestionValue(source, sourceForm, field, value);
		}
	}

	private buildSourceDrafts(): MappingSourceDraft[] {
		return this.data.analysis.sources.map((source, sourceIndex) => {
			const sourceForm = this.getSourceForm(sourceIndex);
			const fieldsForm = sourceForm.get('fields') as UntypedFormGroup;
			const visibleFields = this.getVisibleFields(sourceIndex);

			return {
				benefitKind: sourceForm.get('benefitKind')?.value ?? null,
				fieldValues: visibleFields.reduce<
					Partial<
						Record<BeneficiaryReconciliationMappingCanonicalField, unknown>
					>
				>((fieldValues, field) => {
					fieldValues[field] = fieldsForm.get(field)?.value;

					return fieldValues;
				}, {}),
				source,
				titularityKind: sourceForm.get('titularityKind')?.value ?? null,
				usage:
					sourceForm.get('usage')?.value ??
					BeneficiaryReconciliationMappingSourceUsage.USE,
				visibleFields,
			};
		});
	}

	private clearInvalidContextualSuggestions(
		source: BeneficiaryReconciliationMappingSourceAnalysis,
		sourceForm: UntypedFormGroup,
		benefitKind: BeneficiaryReconciliationMappingBenefitKind | null,
	): void {
		const validFieldSet = new Set(
			this.getContextualSuggestionFields(benefitKind),
		);
		const fieldsForm = sourceForm.get('fields') as UntypedFormGroup;

		CONTEXTUAL_BENEFIT_FIELDS.forEach((field) => {
			const key = this.getContextualSuggestionKey(source, field);
			const value = this.contextualSuggestionValues.get(key);
			const control = fieldsForm.get(field) as UntypedFormControl | null;

			if (!value || !control) {
				return;
			}

			if (!validFieldSet.has(field) && control.value === value) {
				control.setValue('', { emitEvent: false });
			}

			if (!validFieldSet.has(field) || control.value !== value) {
				this.contextualSuggestionValues.delete(key);
			}
		});
	}

	private createFieldFormGroup(
		source: BeneficiaryReconciliationMappingSourceAnalysis,
	): UntypedFormGroup {
		const controls = this.fieldMetadata.reduce<
			Record<string, UntypedFormControl>
		>((accumulator, fieldMetadata) => {
			accumulator[fieldMetadata.value] = this.fb.control(
				this.getSuggestedFieldValue(source, fieldMetadata.value),
			);

			return accumulator;
		}, {});

		return this.fb.group(controls);
	}

	private createSourceFormGroup(
		source: BeneficiaryReconciliationMappingSourceAnalysis,
	): UntypedFormGroup {
		const sourceForm = this.fb.group({
			benefitKind: [null],
			fields: this.createFieldFormGroup(source),
			titularityKind: [null],
			usage: [BeneficiaryReconciliationMappingSourceUsage.USE],
		});

		sourceForm.get('benefitKind')?.valueChanges.subscribe(() => {
			this.applyContextualBenefitSuggestions(source, sourceForm);
		});
		this.applyContextualBenefitSuggestions(source, sourceForm);

		return sourceForm;
	}

	private findContextualColumn(
		source: BeneficiaryReconciliationMappingSourceAnalysis,
		aliasGroups: ContextualColumnAliasGroups,
	): BeneficiaryReconciliationMappingColumn | null {
		const columns = this.getSourceOptionColumns(source);

		for (const aliases of aliasGroups) {
			const normalizedAliases = new Set(
				aliases.map((alias) => this.normalizeContextualColumnName(alias)),
			);
			const matchingColumn = columns.find((column) =>
				normalizedAliases.has(
					this.normalizeContextualColumnName(
						column.normalizedName || column.sourceName,
					),
				),
			);

			if (matchingColumn) {
				return matchingColumn;
			}
		}

		return null;
	}

	private getContextualSuggestionFields(
		benefitKind: BeneficiaryReconciliationMappingBenefitKind | null,
	): BeneficiaryReconciliationMappingCanonicalField[] {
		switch (benefitKind) {
			case BeneficiaryReconciliationMappingBenefitKind.HEALTH:
				return [
					BeneficiaryReconciliationMappingCanonicalField.HEALTH_PLAN_NAME,
					BeneficiaryReconciliationMappingCanonicalField.HEALTH_CARD_NUMBER,
				];
			case BeneficiaryReconciliationMappingBenefitKind.DENTAL:
				return [
					BeneficiaryReconciliationMappingCanonicalField.DENTAL_PLAN_NAME,
					BeneficiaryReconciliationMappingCanonicalField.DENTAL_CARD_NUMBER,
				];
			case BeneficiaryReconciliationMappingBenefitKind.CONJUGATED:
				return [
					BeneficiaryReconciliationMappingCanonicalField.CONJUGATED_PLAN_NAME,
					BeneficiaryReconciliationMappingCanonicalField.CONJUGATED_CARD_NUMBER,
				];
			default:
				return [];
		}
	}

	private getContextualSuggestionKey(
		source: BeneficiaryReconciliationMappingSourceAnalysis,
		field: BeneficiaryReconciliationMappingCanonicalField,
	): string {
		return `${source.sourceId}:${field}`;
	}

	private getExcelColumnLetter(columnIndex: number): string {
		let columnNumber = columnIndex + 1;
		let label = '';

		while (columnNumber > 0) {
			const remainder = (columnNumber - 1) % EXCEL_COLUMN_ALPHABET_LENGTH;
			label =
				String.fromCharCode(EXCEL_COLUMN_FIRST_LETTER_CODE + remainder) + label;
			columnNumber = Math.floor(
				(columnNumber - 1) / EXCEL_COLUMN_ALPHABET_LENGTH,
			);
		}

		return label;
	}

	private getRequiredFields(
		sourceIndex: number,
	): BeneficiaryReconciliationMappingCanonicalField[] {
		const sourceForm = this.getSourceForm(sourceIndex);

		return getMappingRequiredFields(
			sourceForm.get('benefitKind')?.value ?? null,
			sourceForm.get('titularityKind')?.value ?? null,
		);
	}

	private getSuggestedFieldValue(
		source: BeneficiaryReconciliationMappingSourceAnalysis,
		field: BeneficiaryReconciliationMappingCanonicalField,
	): string {
		const suggestion = source.suggestions?.find(
			(item) => item.field === field && item.column,
		);
		const suggestedColumn = suggestion?.column ?? '';

		if (
			suggestedColumn &&
			source.columns.some((column) => column.sourceName === suggestedColumn)
		) {
			return this.getColumnOptionValue(suggestedColumn);
		}

		if (mappingFieldAllowsNotApplicable(field)) {
			return this.getFieldStateOptionValue(
				BeneficiaryReconciliationMappingFieldState.NOT_APPLICABLE,
			);
		}

		return '';
	}

	private getVisibleFields(
		sourceIndex: number,
	): BeneficiaryReconciliationMappingCanonicalField[] {
		const sourceForm = this.getSourceForm(sourceIndex);

		return getVisibleMappingFields(
			sourceForm.get('benefitKind')?.value ?? null,
			sourceForm.get('titularityKind')?.value ?? null,
		);
	}

	private normalizeContextualColumnName(value: string): string {
		return value
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.toLowerCase()
			.replace(/[^a-z0-9]/g, '');
	}

	private setContextualSuggestionValue(
		source: BeneficiaryReconciliationMappingSourceAnalysis,
		sourceForm: UntypedFormGroup,
		field: BeneficiaryReconciliationMappingCanonicalField,
		value: string,
	): void {
		const control = sourceForm
			.get('fields')
			?.get(field) as UntypedFormControl | null;

		if (!control) {
			return;
		}

		this.contextualSuggestionValues.set(
			this.getContextualSuggestionKey(source, field),
			value,
		);
		control.setValue(value, { emitEvent: false });
	}

	private syncConjugatedPrincipalDefaults(
		sourceForm: UntypedFormGroup,
		benefitKind: BeneficiaryReconciliationMappingBenefitKind | null,
	): void {
		if (
			benefitKind !== BeneficiaryReconciliationMappingBenefitKind.CONJUGATED
		) {
			return;
		}

		const notApplicableValue = this.getFieldStateOptionValue(
			BeneficiaryReconciliationMappingFieldState.NOT_APPLICABLE,
		);
		const fieldsForm = sourceForm.get('fields') as UntypedFormGroup;

		[
			BeneficiaryReconciliationMappingCanonicalField.CONJUGATED_PLAN_NAME,
			BeneficiaryReconciliationMappingCanonicalField.CONJUGATED_CARD_NUMBER,
		].forEach((field) => {
			const control = fieldsForm.get(field) as UntypedFormControl | null;

			if (control?.value === notApplicableValue) {
				control.setValue('', { emitEvent: false });
			}
		});
	}
}
